import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import {
  AlertTriangle, ArrowLeft, Boxes, ClipboardList, Info, LayoutDashboard, ShoppingCart, Stethoscope, Tags, Users,
} from 'lucide-react';
import { reportsAPI } from '../../../services/api';
import useReport from '../shared/useReport';
import useDrill from '../shared/useDrill';
import KpiStrip from '../shared/KpiStrip';
import ReportTable from '../shared/ReportTable';
import ReportPrintLayout from '../shared/ReportPrintLayout';
import PrintButton from '../shared/PrintButton';
import ChartPanel from '../shared/ChartPanel';
import { getPresetRange } from '../shared/DateRangeFilter';
import { PALETTE, axisProps, fmtMoney, fmtNum, tooltipStyle } from '../shared/chartTheme';
import DatePicker from '../../DatePicker';
import { clinicToday, formatClinicDate, formatClinicTime } from '../../../utils/clinicTime';
import {
  BEDSIDE_ROLE_LABEL, CATEGORY_LABEL, COST_BASIS_LABEL, DASH, MOVEMENT_REASON_LABEL, OUTCOME_LABEL, OUTCOME_TONE,
  PACKAGING_LABEL, STOCK_STATUS_LABEL, StatusBadge, col, fmtDate, fmtPercent, forPrint, human,
} from './medicineFormat';

// The second half of the Medicine Profile: everything about one item. What arrives depends on the
// user: patients, prescriptions and doctors only with the Clinical reports permission, what the
// hospital earned only with Finance. The server leaves out what may not be seen; this screen only
// shows what arrived.

// Two series on one chart are emerald and violet: the pair clears the colour-blind and contrast
// checks, where emerald beside the palette's teal does not (the two read as one colour).
const SERIES_A = PALETTE[0];
const SERIES_B = PALETTE[3];

const PERIODS = [
  { key: 'all', label: 'All time' },
  { key: 'thisYear', label: 'This year' },
  { key: 'last6Months', label: 'Last 6 months' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

const has = (rows) => Array.isArray(rows) && rows.length > 0;
// The shared date format adds a year and a day unless told not to: "15 Sept", "Sept 2026".
const shortDate = (v) => formatClinicDate(v, { day: 'numeric', month: 'short', year: undefined }, '');
const monthLabel = (m) => formatClinicDate(`${m}-01`, { day: undefined, month: 'short', year: 'numeric' }, m);

// ---------------------------------------------------------------- small pieces

const Banner = ({ tone = 'amber', children }) => {
  const cls = {
    amber: 'bg-amber-50 border-amber-500 text-amber-900',
    blue: 'bg-sky-50 border-sky-500 text-sky-900',
    grey: 'bg-gray-50 border-gray-400 text-gray-700',
  }[tone];
  return (
    <div className={`border-l-4 p-4 rounded-lg text-sm flex items-start gap-2 ${cls}`}>
      {tone === 'amber' ? <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />}
      <div>{children}</div>
    </div>
  );
};

/** Label and value rows: the stock position and the money, as a reader goes down them. */
const Facts = ({ title, rows, notes = [] }) => (
  <div className="bg-white rounded-2xl shadow p-5">
    <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
    <table className="w-full text-sm">
      <tbody className="divide-y divide-gray-100">
        {rows.map((r) => (
          <tr key={r.label}>
            <td className={`py-2 pr-3 ${r.strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
              {r.label}
              {r.sub && <div className="text-xs font-normal text-gray-400">{r.sub}</div>}
            </td>
            <td className={`py-2 text-right whitespace-nowrap ${r.strong ? 'font-bold text-gray-900' : 'text-gray-900'} ${r.alert ? 'text-rose-700' : ''}`}>
              {r.value}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {notes.filter(Boolean).length > 0 && (
      <ul className="mt-3 space-y-1 text-xs text-gray-500 list-disc pl-4">
        {notes.filter(Boolean).map((n) => <li key={n}>{n}</li>)}
      </ul>
    )}
  </div>
);

const stockFacts = (s, summary) => [
  { label: 'In stock now', value: fmtNum(s.current), sub: s.legacyCounter ? 'Hand-kept count' : `${fmtNum(s.activeBatches)} batch${s.activeBatches === 1 ? '' : 'es'}`, strong: true },
  ...(Number(s.expired) > 0
    ? [
        { label: 'Of which expired', value: fmtNum(s.expired), sub: `${fmtMoney(s.expiredValue)} at cost, still counted in stock`, alert: true },
        { label: 'Usable', value: fmtNum(s.usable) },
      ]
    : []),
  { label: 'Value at cost', value: fmtMoney(s.valueAtCost) },
  { label: 'Value at selling price', value: fmtMoney(s.valueAtSale) },
  { label: 'Next expiry', value: fmtDate(s.nextExpiry) },
  { label: 'Reorder level', value: fmtNum(s.threshold) },
  { label: 'Given per day', value: Number(s.perDay) > 0 ? fmtNum(s.perDay) : DASH, sub: `${units(s.usedInWindow)} over the last ${plural(s.windowDays, 'day')}` },
  { label: 'Days of stock left', value: s.daysLeft === null || s.daysLeft === undefined ? DASH : fmtNum(s.daysLeft), sub: 'At that rate, usable stock only' },
  { label: 'Prescribed, not yet given', value: fmtNum(summary.openDemand), sub: 'Finished prescriptions still waiting for a paid bill' },
];

const moneyFacts = (m) => ({
  rows: [
    { label: 'Units sold', value: fmtNum(m.units) },
    { label: 'Charged before discount', value: fmtMoney(m.gross) },
    { label: 'Discounts given', value: Number(m.discount) > 0 ? `- ${fmtMoney(m.discount)}` : fmtMoney(0) },
    { label: 'Revenue', value: fmtMoney(m.revenue), strong: true },
    { label: 'Cost of the units sold', value: fmtMoney(m.costOfGoods), sub: `${units(m.costedUnits)} with a known cost` },
    {
      label: 'Margin',
      value: `${fmtMoney(m.margin)}${m.marginPercent !== null && m.marginPercent !== undefined ? ` (${fmtPercent(m.marginPercent)})` : ''}`,
      strong: true,
      alert: Number(m.margin) < 0,
    },
  ],
  notes: [
    'Cost comes from the batches each sale was taken from, first expiry first, at what those batches cost.',
    Number(m.estimatedUnits) > 0 &&
      `${units(m.estimatedUnits)} could not be traced to a batch and ${Number(m.estimatedUnits) === 1 ? 'is' : 'are'} costed at ${
        m.estimatedFromCatalogue ? 'the catalogue cost, as the item has no real purchase on record' : 'the latest purchase cost'
      }.`,
    Number(m.uncostedUnits) > 0 && `${units(m.uncostedUnits)} ${Number(m.uncostedUnits) === 1 ? 'has' : 'have'} no cost: sold on a bill paid before stock deduction began, or with no purchase cost on record. That revenue is left out of the margin.`,
    'Revenue is the bill lines; round-off on a whole bill is not shared out to its items.',
  ],
});

/** "1 bill", "5 bills". */
const plural = (n, one, many = `${one}s`) => `${fmtNum(n)} ${Number(n) === 1 ? one : many}`;

/** "1 unit", "5 units". */
const units = (n) => plural(n, 'unit');

/** The Prices panel, on screen and in the printout, for readers who do not see the money. */
const priceFacts = (p) => [
  { label: 'Selling price', value: fmtMoney(p.sellingPrice), strong: true },
  {
    label: p.costFromCatalogue ? 'Catalogue cost' : 'Latest purchase cost',
    value: p.latestCost ? fmtMoney(p.latestCost) : DASH,
    sub: p.costFromCatalogue ? 'Never bought on a stock receipt' : p.latestCostDate ? fmtDate(p.latestCostDate) : undefined,
  },
  { label: 'Markup on it', value: fmtPercent(p.markupPercent) },
  { label: 'Lowest and highest cost', value: p.minCost ? `${fmtMoney(p.minCost)} to ${fmtMoney(p.maxCost)}` : DASH },
  {
    label: 'Charged to patients',
    value: p.minCharged
      ? Number(p.minCharged) === Number(p.maxCharged)
        ? fmtMoney(p.minCharged)
        : `${fmtMoney(p.minCharged)} to ${fmtMoney(p.maxCharged)}`
      : DASH,
  },
];

/** A patient as two lines on screen (name over MRN) and one line in the CSV and printout. */
const patientCol = () =>
  col('patientName', 'Patient', 'wrap', {
    text: (r) => (r.patientName ? `${r.patientName} (${r.mrn})` : DASH),
    render: (r) =>
      r.patientName ? (
        <div className="whitespace-nowrap">
          <div>{r.patientName}</div>
          <div className="font-mono text-xs text-gray-500">{r.mrn}</div>
        </div>
      ) : (
        DASH
      ),
  });

// ---------------------------------------------------------------- charts

// A filled marker with a ring of the card's white, so points on crossing lines stay apart.
const markerOf = (fill) => ({ r: 4, fill, stroke: '#fff', strokeWidth: 2 });

// Legend labels stay in ink; the coloured dot beside each carries which series it is.
const legendText = (value) => <span style={{ color: '#374151' }}>{value}</span>;

const StockTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={tooltipStyle.contentStyle} className="px-3 py-2">
      <div className="text-xs text-gray-500">{fmtDate(p.date)}</div>
      <div className="text-sm font-semibold text-gray-900">{fmtNum(p.closing)} in stock</div>
      {(Number(p.in) !== 0 || Number(p.out) > 0 || Number(p.corrected) !== 0) && (
        <div className="text-xs text-gray-600">
          {[
            Number(p.in) !== 0 && `+${fmtNum(p.in)} received`,
            Number(p.out) > 0 && `-${fmtNum(p.out)} given`,
            Number(p.corrected) !== 0 &&
              `${Number(p.corrected) > 0 ? '+' : '-'}${fmtNum(Math.abs(Number(p.corrected)))} receipt correction`,
          ]
            .filter(Boolean)
            .join(', ')}
        </div>
      )}
    </div>
  );
};

const StockChart = ({ series }) => (
  <ResponsiveContainer width="100%" height={240}>
    <LineChart data={series} margin={{ left: 0, right: 16, top: 8 }}>
      <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
      <XAxis dataKey="date" {...axisProps} tickFormatter={shortDate} minTickGap={24} />
      <YAxis {...axisProps} allowDecimals={false} width={48} />
      <Tooltip content={<StockTooltip />} cursor={{ stroke: '#9ca3af', strokeDasharray: '3 3' }} />
      <Line type="stepAfter" dataKey="closing" stroke={SERIES_A} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: SERIES_A, stroke: '#fff', strokeWidth: 2 }} isAnimationActive={false} />
    </LineChart>
  </ResponsiveContainer>
);

const MonthlyChart = ({ monthly }) => (
  <ResponsiveContainer width="100%" height={240}>
    <BarChart data={monthly} margin={{ left: 0, right: 16, top: 8 }} barGap={2}>
      <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
      <XAxis dataKey="month" {...axisProps} tickFormatter={monthLabel} />
      <YAxis {...axisProps} allowDecimals={false} width={48} />
      <Tooltip {...tooltipStyle} itemSorter={null} labelFormatter={monthLabel} formatter={(v, name) => [fmtNum(v), name]} />
      <Legend iconType="circle" itemSorter={null} formatter={legendText} />
      <Bar dataKey="received" name="Received" fill={SERIES_A} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
      <Bar dataKey="given" name="Given to patients" fill={SERIES_B} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
    </BarChart>
  </ResponsiveContainer>
);

const PriceChart = ({ points }) => (
  <ResponsiveContainer width="100%" height={240}>
    <LineChart data={points} margin={{ left: 0, right: 16, top: 8 }}>
      <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
      <XAxis dataKey="label" {...axisProps} minTickGap={16} />
      <YAxis {...axisProps} width={56} tickFormatter={(v) => fmtNum(v)} />
      <Tooltip {...tooltipStyle} itemSorter={null} formatter={(v, name) => [fmtMoney(v), name]} labelFormatter={(l, p) => p?.[0]?.payload?.tip || l} />
      <Legend iconType="circle" itemSorter={null} formatter={legendText} />
      <Line type="linear" dataKey="cost" name="Cost per unit" stroke={SERIES_A} strokeWidth={2} dot={markerOf(SERIES_A)} activeDot={markerOf(SERIES_A)} connectNulls isAnimationActive={false} />
      <Line type="linear" dataKey="sell" name="Selling price set" stroke={SERIES_B} strokeWidth={2} dot={markerOf(SERIES_B)} activeDot={markerOf(SERIES_B)} connectNulls isAnimationActive={false} />
    </LineChart>
  </ResponsiveContainer>
);

// ---------------------------------------------------------------- columns

const expiryText = (r, today) => {
  if (!r.expiryDate) return 'No expiry recorded';
  const expired = r.expiryDate < today;
  return `${fmtDate(r.expiryDate)}${expired ? ' (expired)' : ''}`;
};

const batchColumns = (today) => [
  col('invoiceDate', 'Received', 'date'),
  col('vendor', 'Supplier', 'wrap'),
  col('invoiceNumber', 'Supplier Invoice', 'mono'),
  col('batch', 'Batch', 'mono'),
  col('expiryDate', 'Expiry', 'text', {
    text: (r) => expiryText(r, today),
    render: (r) => (r.expiryDate && r.expiryDate < today ? <span className="text-rose-700 font-medium">{expiryText(r, today)}</span> : expiryText(r, today)),
  }),
  col('received', 'Received Qty', 'number'),
  col('remaining', 'Left', 'number'),
  col('effectiveCost', 'Cost per Unit', 'money'),
  col('sellingPrice', 'Selling Price', 'money'),
];

const chargeText = (pct, amount) => {
  if (pct !== null && pct !== undefined && Number(pct) !== 0) return `${fmtNum(pct)}%`;
  if (amount !== null && amount !== undefined && Number(amount) !== 0) return fmtMoney(amount);
  return null;
};

const lineNote = (r) =>
  r.receiptStatus !== 'ACTIVE' ? 'Receipt cancelled' : r.openingBalance ? 'Opening balance' : r.billAttached ? 'Bill attached' : null;

// Columns empty for every line of this item are left out (most items carry no discount or tax),
// so the rest fit the page.
const lineColumns = (lines, today) => {
  const rows = lines || [];
  const any = (pred) => rows.some(pred);
  return [
    col('invoiceDate', 'Received', 'date'),
    col('vendor', 'Supplier', 'text'),
    col('invoiceNumber', 'Supplier Invoice', 'mono'),
    col('batch', 'Batch', 'mono'),
    col('expiryDate', 'Expiry', 'text', { text: (r) => expiryText(r, today) }),
    col('received', 'Received', 'number'),
    col('consumed', 'Used', 'number'),
    col('remaining', 'Left', 'number'),
    ...(any((r) => r.listPrice !== null && r.listPrice !== undefined) ? [col('listPrice', 'Invoice Rate', 'money')] : []),
    ...(any((r) => chargeText(r.discountPercent, r.discountAmount)) ? [col((r) => chargeText(r.discountPercent, r.discountAmount), 'Discount', 'text')] : []),
    ...(any((r) => chargeText(r.taxPercent, r.taxAmount)) ? [col((r) => chargeText(r.taxPercent, r.taxAmount), 'Tax', 'text')] : []),
    col('unitCost', 'Cost per Unit', 'money'),
    col('sellingPrice', 'Selling Price', 'money'),
    ...(any(lineNote) ? [col((r) => lineNote(r), 'Note', 'text')] : []),
  ];
};

const purchaseColumns = () => [
  col('invoiceDate', 'Date', 'date'),
  col('invoiceNumber', 'Supplier Invoice', 'mono'),
  col('vendor', 'Supplier', 'wrap'),
  col('quantity', 'Units', 'number'),
  col('lineTotal', 'Paid', 'money'),
  col('unitCost', 'Cost per Unit', 'money'),
  col('changePercent', 'Change', 'percent', {
    render: (r) =>
      r.changePercent === null || r.changePercent === undefined ? DASH : (
        <span className={Number(r.changePercent) > 0 ? 'text-rose-700 font-medium' : Number(r.changePercent) < 0 ? 'text-emerald-700 font-medium' : ''}>
          {fmtPercent(r.changePercent, true)}
        </span>
      ),
  }),
  col('sellingPrice', 'Selling Price Set', 'money'),
  col('markupPercent', 'Markup', 'percent', { text: (r) => fmtPercent(r.markupPercent) }),
  col('batches', 'Batches', 'wrap'),
  col(
    (r) => (r.cancelled ? 'Cancelled' : r.openingBalance ? 'Opening balance' : r.free ? 'Given free' : null),
    'Note',
    'text'
  ),
];

const vendorColumns = () => [
  col('vendor', 'Supplier', 'wrap'),
  col('deliveries', 'Deliveries', 'number'),
  col('units', 'Units', 'number'),
  col('spent', 'Spent', 'money'),
  col('minCost', 'Lowest Cost', 'money'),
  col('averageCost', 'Average Cost', 'money'),
  col('maxCost', 'Highest Cost', 'money'),
  col('lastCost', 'Last Cost', 'money'),
  col('lastDate', 'Last Delivery', 'date'),
];

const chargedColumns = () => [
  col('unitPrice', 'Price per Unit', 'money'),
  col('bills', 'Bills', 'number'),
  col('units', 'Units', 'number'),
  col('first', 'First Charged', 'date'),
  col('last', 'Last Charged', 'date'),
];

const ledgerColumns = (clinical) => [
  col('at', 'When', 'datetime'),
  col((r) => MOVEMENT_REASON_LABEL[r.reason] || human(r.reason), 'What Happened', 'text'),
  col('delta', 'Change', 'signedNum', {
    render: (r) => (
      <span className={Number(r.delta) > 0 ? 'text-emerald-700 font-medium' : 'text-gray-900'}>
        {Number(r.delta) > 0 ? `+${fmtNum(r.delta)}` : fmtNum(r.delta)}
      </span>
    ),
  }),
  col('balanceAfter', 'Stock After', 'number'),
  col((r) => r.invoiceNumber || [r.receiptNumber, r.vendor].filter(Boolean).join(', ') || null, 'Bill or Receipt', 'wrap'),
  ...(clinical ? [patientCol()] : []),
  col('batchDetail', 'Batches', 'wrap'),
  col('performedBy', 'By', 'text'),
];

/** What a sale's stock row says: deducted (and how its cost was found) or never taken off. */
const stockNote = (r, money) =>
  r.movementId ? (money ? COST_BASIS_LABEL[r.costBasis] || null : 'Deducted from stock') : 'Stock not deducted';

// Kept to ten columns at most so the table fits the page: the date and time share a cell, as do
// the bill and its prescription, and how a sale was costed sits under the batches it came from.
const dispensedColumns = (clinical, money) => [
  col('givenAt', 'Given', 'datetime', {
    render: (r) => (
      <div className="whitespace-nowrap">
        <div>{fmtDate(r.givenAt)}</div>
        <div className="text-xs text-gray-500">{formatClinicTime(r.givenAt, {}, '')}</div>
      </div>
    ),
  }),
  col('invoiceNumber', clinical ? 'Bill / Prescription' : 'Bill', 'mono', {
    text: (r) => [r.invoiceNumber, clinical ? r.prescriptionNumber : null].filter(Boolean).join(' / '),
    render: (r) => (
      <div className="font-mono text-xs whitespace-nowrap">
        <div>{r.invoiceNumber}</div>
        {clinical && r.prescriptionNumber && <div className="text-gray-500">{r.prescriptionNumber}</div>}
      </div>
    ),
  }),
  ...(clinical ? [patientCol(), col('doctor', 'Doctor', 'text')] : []),
  col('quantity', 'Qty', 'number'),
  ...(money
    ? [
        col('discount', 'Discount', 'money'),
        col('total', 'Charged', 'money'),
        col('cost', 'Cost', 'money'),
        col('margin', 'Margin', 'money', {
          render: (r) => (r.margin === null || r.margin === undefined ? DASH : <span className={Number(r.margin) < 0 ? 'text-rose-700 font-medium' : ''}>{fmtMoney(r.margin)}</span>),
        }),
      ]
    : [col('unitPrice', 'Price per Unit', 'money')]),
  col('batchDetail', 'Batches', 'wrap', {
    text: (r) => [r.batchDetail, stockNote(r, money)].filter(Boolean).join('. '),
    render: (r) => (
      <div>
        {r.batchDetail && <div className="font-mono text-xs">{r.batchDetail}</div>}
        <div className={`text-xs ${r.movementId ? 'text-gray-500' : 'text-amber-700'}`}>{stockNote(r, money)}</div>
      </div>
    ),
  }),
];

const patientColumns = (money) => [
  col((r) => r.patientName, 'Patient', 'wrap', { text: (r) => `${r.patientName || DASH}${r.patientArchived ? ' (archived)' : ''}` }),
  col('mrn', 'MRN', 'mono'),
  col('timesPrescribed', 'Prescribed', 'number'),
  col('bills', 'Bills', 'number'),
  col('unitsGiven', 'Units Given', 'number'),
  ...(money ? [col('amountPaid', 'Paid', 'money')] : []),
  col('firstGiven', 'First Given', 'date'),
  col('lastGiven', 'Last Given', 'date'),
];

const courseText = (r) => {
  if (!r.durationUnit) return r.durationValue ? String(r.durationValue) : null;
  if (r.durationUnit === 'CONTINUOUS') return r.durationValue ? `Continuous, ${plural(r.durationValue, 'day')}' supply` : 'Continuous';
  if (r.durationUnit === 'UNTIL_FINISHED') return 'Until finished';
  if (!r.durationValue) return human(r.durationUnit);
  const unit = human(r.durationUnit).toLowerCase();
  return `${r.durationValue} ${Number(r.durationValue) === 1 ? unit.replace(/s$/, '') : unit}`;
};

const prescribedColumns = () => [
  col('date', 'Date', 'date'),
  col('prescriptionNumber', 'Prescription', 'mono'),
  patientCol(),
  col('doctor', 'Doctor', 'text'),
  col('dosage', 'Dosage', 'wrap'),
  col((r) => courseText(r), 'Course', 'text'),
  col('quantityPrescribed', 'Qty Prescribed', 'number'),
  col((r) => OUTCOME_LABEL[r.outcome] || human(r.outcome), 'What Happened', 'text', {
    render: (r) => (
      <span className="inline-flex flex-wrap items-center gap-1">
        <StatusBadge label={OUTCOME_LABEL[r.outcome] || human(r.outcome)} tone={OUTCOME_TONE[r.outcome]} />
        {r.outcome === 'GIVEN' && r.quantityBilled !== null && r.quantityBilled !== undefined && (
          <span className="text-xs text-gray-500">{fmtNum(r.quantityBilled)} given</span>
        )}
      </span>
    ),
    text: (r) =>
      `${OUTCOME_LABEL[r.outcome] || human(r.outcome)}${r.outcome === 'GIVEN' && r.quantityBilled ? `, ${r.quantityBilled} given` : ''}`,
  }),
  col('invoiceNumber', 'Bill', 'mono'),
];

const outsideColumns = () => [
  col('date', 'Date', 'date'),
  col('prescriptionNumber', 'Prescription', 'mono'),
  patientCol(),
  col('doctor', 'Doctor', 'text'),
  col((r) => [r.writtenAs, r.strength].filter(Boolean).join(' '), 'Written As', 'wrap'),
  col('dosage', 'Dosage', 'wrap'),
];

const doctorColumns = () => [
  col('doctor', 'Doctor', 'wrap'),
  col('prescriptions', 'Prescriptions', 'number'),
  col('patients', 'Patients', 'number'),
  col('lastPrescribed', 'Last Prescribed', 'date'),
];

const dosageColumns = () => [
  col('dose', 'Dose', 'wrap'),
  col('course', 'Course', 'wrap'),
  col((r) => (r.timing ? human(r.timing) : null), 'Timing', 'text'),
  col('count', 'Times Written', 'number'),
  col('share', 'Share', 'percent', { text: (r) => fmtPercent(r.share) }),
];

const coPrescribedColumns = () => [
  col((r) => [r.name, r.strength].filter(Boolean).join(' '), 'Written With', 'wrap'),
  col('code', 'Code', 'mono'),
  col((r) => (r.outside ? 'Bought outside' : 'Hospital stock'), 'Source', 'text'),
  col('prescriptions', 'Shared Prescriptions', 'number'),
];

const orderColumns = () => [
  col('orderDate', 'Ordered', 'date'),
  col('orderNumber', 'Order', 'mono'),
  col('vendor', 'Supplier', 'wrap'),
  col('status', 'Status', 'status'),
  col('quantity', 'Ordered Qty', 'number'),
  col('received', 'Received Qty', 'number'),
  col('unitPrice', 'Price per Unit', 'money'),
  col('receivedDate', 'Received On', 'date'),
];

const monthlyColumns = (money, clinical) => [
  col((r) => monthLabel(r.month), 'Month', 'text'),
  col('received', 'Received', 'signedNum', { text: (r) => fmtNum(r.received) }),
  col('given', 'Given', 'number'),
  col('bills', 'Bills', 'number'),
  ...(clinical ? [col('prescriptions', 'Prescriptions', 'number')] : []),
  ...(money ? [col('revenue', 'Revenue', 'money')] : []),
];

// ---------------------------------------------------------------- identity

const packText = (m) => {
  if (m.dispenseSize && m.packSize) {
    const unit = m.sizeUnit || '';
    return `Received as ${fmtNum(m.packSize)} ${unit}, given as ${fmtNum(m.dispenseSize)} ${unit} (${fmtNum(m.unitsPerPack)} per pack)`;
  }
  if (Number(m.unitsPerPack) > 1) return `${fmtNum(m.unitsPerPack)} units per pack`;
  return null;
};

const identityFields = (m) =>
  [
    ['Code', m.code],
    ['Generic Name', m.genericName],
    ['Manufacturer', m.manufacturer],
    ['Kind', CATEGORY_LABEL[m.category] || human(m.category)],
    ['Form', human(m.type)],
    ['Packaging', PACKAGING_LABEL[m.packaging] || human(m.packaging)],
    ['Strength', m.strength],
    ['Container Size', m.containerSize],
    ['Pack', packText(m)],
    ['Storage Location', m.storageLocation],
    ['Default Supplier', m.defaultVendor],
    ['Reorder Level', fmtNum(m.threshold)],
    ['In the Catalogue Since', fmtDate(m.createdAt)],
    ...(has(m.bedsideRoles) ? [['Settings Role', `Used as ${m.bedsideRoles.map((r) => BEDSIDE_ROLE_LABEL[r] || r).join(' and ')}`]] : []),
  ].filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== DASH);

// ---------------------------------------------------------------- the view

const MedicineProfileView = ({ medicineId, onBack }) => {
  const drill = useDrill();
  const tabBarRef = useRef(null);
  const [period, setPeriod] = useState({ key: 'all', from: '', to: '' });
  const [tab, setTab] = useState('overview');
  const today = clinicToday();

  const fetcher = useCallback(
    () => reportsAPI.getMedicineProfile({ medicineId, from: period.from || undefined, to: period.to || undefined }),
    [medicineId, period.from, period.to]
  );
  const { data, loading } = useReport(fetcher);

  const choosePeriod = (key) => {
    if (key === 'all') setPeriod({ key, from: '', to: '' });
    else if (key === 'custom') setPeriod((p) => ({ ...p, key }));
    else setPeriod({ key, ...getPresetRange(key) });
  };

  const openTab = (id) => {
    setTab(id);
    const bar = tabBarRef.current;
    if (bar && bar.getBoundingClientRect().top < 0) bar.scrollIntoView({ block: 'start' });
  };

  const access = data?.access || {};
  const clinical = Boolean(access.clinical);
  const money = Boolean(access.money);
  const m = data?.medicine;
  const stock = data?.stock;
  const summary = useMemo(() => data?.summary || {}, [data]);
  const prices = useMemo(() => data?.priceSummary || {}, [data]);
  const allTime = data?.period?.allTime !== false;

  const batchesInStock = useMemo(() => (data?.batches || []).filter((b) => Number(b.remaining) > 0), [data]);
  const pricePoints = useMemo(
    () =>
      [...(data?.purchases || [])]
        .filter((p) => !p.cancelled && !p.openingBalance && !p.free && p.unitCost !== null)
        .reverse()
        .map((p) => ({
          label: shortDate(p.invoiceDate),
          tip: `${fmtDate(p.invoiceDate)}, ${p.vendor || 'unknown supplier'}`,
          cost: Number(p.unitCost),
          sell: p.sellingPrice === null || p.sellingPrice === undefined ? null : Number(p.sellingPrice),
        })),
    [data]
  );

  const cols = useMemo(
    () => ({
      batches: batchColumns(today),
      lines: lineColumns(data?.batches, today),
      purchases: purchaseColumns(),
      vendors: vendorColumns(),
      charged: chargedColumns(),
      ledger: ledgerColumns(clinical),
      dispensed: dispensedColumns(clinical, money),
      patients: patientColumns(money),
      prescribed: prescribedColumns(),
      outside: outsideColumns(),
      doctors: doctorColumns(),
      dosages: dosageColumns(),
      coPrescribed: coPrescribedColumns(),
      orders: orderColumns(),
      monthly: monthlyColumns(money, clinical),
    }),
    [today, clinical, money, data?.batches]
  );

  const kpis = useMemo(() => {
    if (!stock) return [];
    const items = [
      {
        label: 'In Stock',
        value: fmtNum(stock.current),
        sub: stock.legacyCounter ? 'Hand-kept count' : Number(stock.expired) > 0 ? `${fmtNum(stock.expired)} of them expired` : `${fmtNum(stock.activeBatches)} batch${Number(stock.activeBatches) === 1 ? '' : 'es'}`,
        tone: Number(stock.usable) <= Number(stock.threshold) ? 'alert' : undefined,
      },
      {
        label: 'Days Left',
        value: stock.daysLeft === null || stock.daysLeft === undefined ? DASH : fmtNum(stock.daysLeft),
        sub: Number(stock.perDay) > 0 ? `${fmtNum(stock.perDay)} given a day` : 'Not given lately',
        tone: stock.daysLeft !== null && stock.daysLeft !== undefined && Number(stock.daysLeft) < 7 ? 'alert' : undefined,
      },
      {
        label: 'Selling Price',
        value: fmtMoney(prices.sellingPrice),
        sub: prices.latestCost ? `${prices.costFromCatalogue ? 'Catalogue cost' : 'Cost'} ${fmtMoney(prices.latestCost)}${prices.markupPercent !== null && prices.markupPercent !== undefined ? `, ${fmtPercent(prices.markupPercent)} markup` : ''}` : 'No purchase cost on record',
      },
      { label: 'Units Given', value: fmtNum(summary.unitsGiven), sub: plural(summary.bills, 'bill') },
    ];
    if (money && data?.money) {
      items.push({ label: 'Revenue', value: fmtMoney(data.money.revenue), sub: `Margin ${fmtMoney(data.money.margin)}` });
    } else {
      items.push({ label: 'Stock Value', value: fmtMoney(stock.valueAtCost), sub: `${fmtMoney(stock.valueAtSale)} at selling price` });
    }
    if (clinical) {
      // The same people as the Patients tab: everyone it was prescribed to or given to.
      items.push({ label: 'Patients', value: fmtNum((data?.patients || []).length), sub: `${plural(summary.timesPrescribed, 'time')} prescribed` });
    } else {
      items.push({ label: 'Waiting', value: fmtNum(summary.openDemand), sub: 'Prescribed, not yet given' });
    }
    return items;
  }, [stock, prices, summary, money, clinical, data]);

  const tabs = useMemo(() => {
    if (!data) return [];
    const n = (l) => (l ? l.length : 0);
    return [
      { id: 'overview', label: 'Overview', icon: LayoutDashboard },
      { id: 'stock', label: 'Stock & Batches', icon: Boxes, count: n(batchesInStock) },
      { id: 'prices', label: 'Purchases & Prices', icon: Tags, count: n(data.purchases) },
      { id: 'dispensing', label: 'Dispensing', icon: ClipboardList, count: n(data.dispensed) },
      clinical && { id: 'patients', label: 'Patients & Prescriptions', icon: Users, count: n(data.patients) },
      clinical && { id: 'prescribing', label: 'Prescribing', icon: Stethoscope, count: n(data.doctors) },
      has(data.orders) && { id: 'orders', label: 'Purchase Orders', icon: ShoppingCart, count: n(data.orders) },
    ].filter(Boolean);
  }, [data, clinical, batchesInStock]);
  const activeTab = tabs.some((t) => t.id === tab) ? tab : 'overview';

  const printTables = useMemo(() => {
    if (!data || !m) return [];
    const t = [];
    const add = (title, columns, rows) => { if (has(rows)) t.push({ title, columns: forPrint(columns), rows }); };
    const kv = [{ key: 'label', label: 'Detail' }, { key: 'value', label: 'Value' }];
    t.push({ title: 'Item details', columns: kv, rows: identityFields(m).map(([label, value]) => ({ label, value })) });
    if (stock) {
      t.push({
        title: 'Stock',
        columns: kv,
        rows: stockFacts(stock, summary).map((r) => ({ label: r.sub ? `${r.label} (${r.sub})` : r.label, value: r.value })),
      });
    }
    if (money && data.money) {
      const mf = moneyFacts(data.money);
      t.push({
        title: 'Sales and margin',
        columns: kv,
        rows: [
          ...mf.rows.map((r) => ({ label: r.sub ? `${r.label} (${r.sub})` : r.label, value: r.value })),
          ...mf.notes.filter(Boolean).map((note) => ({ label: note, value: '' })),
        ],
      });
    } else {
      t.push({
        title: 'Prices',
        columns: kv,
        rows: priceFacts(prices).map((r) => ({ label: r.sub ? `${r.label} (${r.sub})` : r.label, value: r.value })),
      });
    }
    add('Batches in stock', cols.batches, batchesInStock);
    add('Deliveries and cost', cols.purchases, data.purchases);
    add('By supplier', cols.vendors, data.vendors);
    add('Prices charged to patients', cols.charged, data.chargedPrices);
    add('Month by month', cols.monthly, data.monthly);
    add('Given to patients', cols.dispensed, data.dispensed);
    add('Patients', cols.patients, data.patients);
    add('Prescriptions', cols.prescribed, data.prescribed);
    add('Written as an outside purchase', cols.outside, data.outside);
    add('Doctors', cols.doctors, data.doctors);
    add('Dosages', cols.dosages, data.dosages);
    add('Written together with', cols.coPrescribed, data.coPrescribed);
    add('Purchase orders', cols.orders, data.orders);
    add('Stock ledger', cols.ledger, data.movements);
    return t;
  }, [data, m, stock, summary, prices, money, cols, batchesInStock]);

  // ---- render

  if (!data || !m) {
    return (
      <div className="space-y-5">
        <button onClick={onBack} className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 hover:text-emerald-800">
          <ArrowLeft className="w-4 h-4" /> Back to the catalogue
        </button>
        <div className="bg-white rounded-2xl shadow p-12 text-center text-sm text-gray-500">
          {loading ? 'Loading the medicine profile' : 'The medicine profile could not be loaded.'}
        </div>
      </div>
    );
  }

  const title = [m.name, m.strength].filter(Boolean).join(' ');
  const periodText = allTime
    ? 'All time'
    : `${data.period.from ? fmtDate(data.period.from) : 'the start'} to ${data.period.to ? fmtDate(data.period.to) : 'today'}`;
  const fields = identityFields(m);
  const mf = money && data.money ? moneyFacts(data.money) : null;
  const monthlyHasBars = (data.monthly || []).some((r) => Number(r.received) !== 0 || Number(r.given) !== 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow p-5 space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={onBack}
            className="inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-700 rounded-lg px-3 py-1.5 text-sm font-medium hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" /> Back to the catalogue
          </button>
          <div className="ml-auto flex items-center gap-2">
            {loading && <span className="text-xs text-gray-400">Updating</span>}
            <PrintButton />
          </div>
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <span className="font-mono text-sm bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{m.code}</span>
            <StatusBadge value={stock.status} label={STOCK_STATUS_LABEL[stock.status]} />
            {m.discontinued && <StatusBadge label="Discontinued" tone="grey" />}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {[CATEGORY_LABEL[m.category], human(m.type), m.genericName].filter((x) => x && x !== DASH).join(' · ')}
          </p>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-sm">
            {fields
              .filter(([label]) => !['Kind', 'Form', 'Generic Name', 'Code'].includes(label))
              .map(([label, value]) => (
                <div key={label} className="min-w-0">
                  <dt className="text-xs text-gray-500">{label}</dt>
                  <dd className="text-gray-900 break-words">{value}</dd>
                </div>
              ))}
          </dl>
        </div>
      </div>

      {(data.caveats || []).map((c) => <Banner key={c}>{c}</Banner>)}

      {/* Period */}
      <div className="bg-white rounded-2xl shadow p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700 mr-1">Period</span>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              onClick={() => choosePeriod(p.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period.key === p.key ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          {period.key === 'custom' && (
            <div className="flex items-center gap-2">
              <DatePicker value={period.from} onChange={(v) => setPeriod((p) => ({ ...p, from: v }))} max={period.to || today} placeholder="From" />
              <span className="text-gray-400 text-sm">to</span>
              <DatePicker value={period.to} onChange={(v) => setPeriod((p) => ({ ...p, to: v }))} min={period.from || undefined} max={today} placeholder="To" />
            </div>
          )}
        </div>
        {!allTime && (
          <p className="text-xs text-gray-500">
            Deliveries, dispensing, prescriptions and the stock ledger from {periodText}. Stock, batches and value are as they
            stand today.
          </p>
        )}
      </div>

      <KpiStrip items={kpis} loading={loading} />

      <div ref={tabBarRef} className="bg-white rounded-2xl shadow p-2 flex flex-wrap gap-1 scroll-mt-4">
        {tabs.map((t) => {
          const Icon = t.icon;
          const on = activeTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => openTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                on ? 'bg-navy-800 text-white' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
              {t.count !== undefined && (
                <span className={`text-xs rounded-full px-1.5 ${on ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>{t.count}</span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-5">
          <ChartPanel title="Stock over time">
            {has(data.series) ? (
              <>
                <StockChart series={data.series} />
                <p className="text-xs text-gray-500 mt-2">
                  {data.ledgerStart
                    ? `Rebuilt from the stock ledger, which records this item from ${fmtDate(data.ledgerStart)}.${
                        Number(data.openingBeforeLedger) > 0
                          ? ` ${units(data.openingBeforeLedger)} ${Number(data.openingBeforeLedger) === 1 ? 'was' : 'were'} already on hand then.`
                          : ''
                      }`
                    : ''}
                </p>
              </>
            ) : (
              <p className="text-gray-400 text-sm text-center py-12">
                {allTime
                  ? 'No stock movement has been recorded for this item.'
                  : `The stock ledger has no entry for this item in this period.${
                      data.ledgerStart ? ` It records this item from ${fmtDate(data.ledgerStart)}.` : ''
                    }`}
              </p>
            )}
          </ChartPanel>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Facts title="Stock position" rows={stockFacts(stock, summary)} />
            {mf ? (
              <Facts title="Sales and margin" rows={mf.rows} notes={mf.notes} />
            ) : (
              <Facts
                title="Prices"
                rows={priceFacts(prices)}
                notes={['Sales, cost of goods and margin are shown to users with the Finance reports permission.']}
              />
            )}
          </div>
          <ChartPanel title="Month by month">
            {monthlyHasBars ? (
              <MonthlyChart monthly={data.monthly} />
            ) : (
              <p className="text-gray-400 text-sm text-center py-12">No stock received or given to chart in this period.</p>
            )}
          </ChartPanel>
          {has(data.monthly) && (
            <ReportTable
              title="Month by month"
              columns={cols.monthly}
              rows={data.monthly}
              searchable={false}
              csvName={`${m.code}-monthly`}
              footnote="Received is net of cancelled and corrected receipts. Given counts completed bills, by the day they were paid."
            />
          )}
          {!clinical && (
            <Banner tone="grey">Patients, prescriptions and doctors are shown to users with the Clinical reports permission.</Banner>
          )}
        </div>
      )}

      {activeTab === 'stock' && (
        <div className="space-y-5">
          <ReportTable
            title="Batches in stock"
            columns={cols.batches}
            rows={batchesInStock}
            csvName={`${m.code}-batches`}
            emptyText={stock.legacyCounter ? 'This item runs on a hand-kept count and has no batches.' : 'No batch has stock left.'}
            footnote="Given out first expiry first. Expired batches stay counted in stock until removed. Cost per unit is what the delivery cost per unit, so free units carry their delivery's cost."
          />
          <ReportTable
            title="Stock ledger"
            columns={cols.ledger}
            rows={data.movements || []}
            csvName={`${m.code}-ledger`}
            emptyText="No stock movement in this period."
            footnote={
              data.ledgerStart
                ? `Every change to this item's stock since ${fmtDate(data.ledgerStart)}, newest first, with the stock left after each.`
                : 'The stock ledger has recorded nothing for this item.'
            }
          />
          <ReportTable
            title="Every receipt line"
            columns={cols.lines}
            rows={data.batches || []}
            csvName={`${m.code}-receipt-lines`}
            emptyText="Never received on a stock receipt."
            footnote="Including batches used up and receipts cancelled. Cost per unit is the line total over its units, after discount and tax."
          />
        </div>
      )}

      {activeTab === 'prices' && (
        <div className="space-y-5">
          <ChartPanel title="Cost and selling price per unit, delivery by delivery">
            {pricePoints.length >= 2 ? (
              <PriceChart points={pricePoints} />
            ) : (
              <p className="text-gray-400 text-sm text-center py-12">
                {pricePoints.length === 1 ? 'Only one purchase so far, so there is no change to chart yet.' : 'No purchase to chart.'}
              </p>
            )}
          </ChartPanel>
          <ReportTable
            title="Deliveries and cost"
            columns={cols.purchases}
            rows={data.purchases || []}
            csvName={`${m.code}-deliveries`}
            emptyText="No delivery in this period."
            footnote="Change compares each purchase with the one before it. Opening balances, free deliveries and cancelled receipts are listed but not compared."
          />
          <ReportTable title="By supplier" columns={cols.vendors} rows={data.vendors || []} searchable={false} emptyText="No purchase in this period." />
          <ReportTable
            title="Prices charged to patients"
            columns={cols.charged}
            rows={data.chargedPrices || []}
            searchable={false}
            emptyText="Not sold in this period."
            footnote="The price per unit on each completed bill, before any discount."
          />
        </div>
      )}

      {activeTab === 'dispensing' && (
        <ReportTable
          title="Given to patients"
          columns={cols.dispensed}
          rows={data.dispensed || []}
          onRowClick={(r) => drill.open({ type: 'invoice', value: r.invoiceNumber })}
          csvName={`${m.code}-dispensed`}
          emptyText="Not given to anyone in this period."
          footnote="One row per completed bill. Click one to see the bill. A bill paid before stock deduction began was never taken off the stock."
        />
      )}

      {activeTab === 'patients' && clinical && (
        <div className="space-y-5">
          <ReportTable
            title="Patients"
            columns={cols.patients}
            rows={data.patients || []}
            onRowClick={(r) => r.mrn && drill.open({ type: 'patient', mrn: r.mrn })}
            csvName={`${m.code}-patients`}
            emptyText="No patient was prescribed or given this in the period."
            footnote="Everyone it was prescribed to or given to. Click a patient for a summary of their record."
          />
          <ReportTable
            title="Prescriptions"
            columns={cols.prescribed}
            rows={data.prescribed || []}
            csvName={`${m.code}-prescriptions`}
            emptyText="Not prescribed in this period."
            footnote="Every prescription line for this item, with what became of it. Qty Prescribed is worked out from the dose and course."
          />
          {has(data.outside) && (
            <ReportTable
              title="Written as an outside purchase under the same name"
              columns={cols.outside}
              rows={data.outside}
              csvName={`${m.code}-outside`}
              footnote="Prescribed for the patient to buy elsewhere. Matched on the name only, so a different product with the same name would appear here too."
            />
          )}
        </div>
      )}

      {activeTab === 'prescribing' && clinical && (
        <div className="space-y-5">
          <ReportTable title="Doctors who prescribe it" columns={cols.doctors} rows={data.doctors || []} searchable={false} emptyText="Not prescribed in this period." />
          <ReportTable
            title="How it is dosed"
            columns={cols.dosages}
            rows={data.dosages || []}
            searchable={false}
            emptyText="Not prescribed in this period."
            footnote="Doses read morning-afternoon-evening-night."
          />
          <ReportTable
            title="Written together with"
            columns={cols.coPrescribed}
            rows={data.coPrescribed || []}
            searchable={false}
            emptyText="Never written alongside anything else in this period."
            footnote="The items most often on the same prescription, up to 15."
          />
        </div>
      )}

      {activeTab === 'orders' && (
        <ReportTable
          title="Purchase orders"
          columns={cols.orders}
          rows={data.orders || []}
          searchable={false}
          footnote="Receiving an order adds to the hand-kept count only, not to a batch."
        />
      )}

      <ReportPrintLayout
        title={`Medicine Profile: ${title} (${m.code})`}
        subtitle={`Period: ${periodText}`}
        kpis={kpis.map((k) => ({ label: k.label, value: k.value }))}
        tables={printTables}
      />

      {drill.node}
    </div>
  );
};

export default MedicineProfileView;
